const DRAFT_PREFIX = 'future_lens_draft_';
const DRAFT_EXPIRY_DAYS = 7;

interface DraftData {
  data: any;
  timestamp: number;
  userId: string;
}

export const draftStorage = {
  save: (key: string, data: any, userId: string): boolean => {
    try {
      const draftKey = `${DRAFT_PREFIX}${userId}_${key}`;
      const draftData: DraftData = {
        data,
        timestamp: Date.now(),
        userId
      };
      localStorage.setItem(draftKey, JSON.stringify(draftData));
      return true;
    } catch (error) {
      console.error('Error saving draft to localStorage:', error);
      if (error.name === 'QuotaExceededError') {
        draftStorage.cleanupOldDrafts();
      }
      return false;
    }
  },

  load: (key: string, userId: string): any | null => {
    try {
      const draftKey = `${DRAFT_PREFIX}${userId}_${key}`;
      const stored = localStorage.getItem(draftKey);

      if (!stored) return null;

      const draftData: DraftData = JSON.parse(stored);

      if (draftData.userId !== userId) {
        draftStorage.remove(key, userId);
        return null;
      }

      const ageInDays = (Date.now() - draftData.timestamp) / (1000 * 60 * 60 * 24);
      if (ageInDays > DRAFT_EXPIRY_DAYS) {
        draftStorage.remove(key, userId);
        return null;
      }

      return draftData.data;
    } catch (error) {
      console.error('Error loading draft from localStorage:', error);
      return null;
    }
  },

  remove: (key: string, userId: string): void => {
    try {
      const draftKey = `${DRAFT_PREFIX}${userId}_${key}`;
      localStorage.removeItem(draftKey);
    } catch (error) {
      console.error('Error removing draft from localStorage:', error);
    }
  },

  hasDraft: (key: string, userId: string): boolean => {
    try {
      const draftKey = `${DRAFT_PREFIX}${userId}_${key}`;
      return localStorage.getItem(draftKey) !== null;
    } catch (error) {
      return false;
    }
  },

  getDraftAge: (key: string, userId: string): number | null => {
    try {
      const draftKey = `${DRAFT_PREFIX}${userId}_${key}`;
      const stored = localStorage.getItem(draftKey);

      if (!stored) return null;

      const draftData: DraftData = JSON.parse(stored);
      return Date.now() - draftData.timestamp;
    } catch (error) {
      return null;
    }
  },

  cleanupOldDrafts: (): void => {
    try {
      const keys = Object.keys(localStorage);
      const draftKeys = keys.filter(key => key.startsWith(DRAFT_PREFIX));

      draftKeys.forEach(key => {
        try {
          const stored = localStorage.getItem(key);
          if (!stored) return;

          const draftData: DraftData = JSON.parse(stored);
          const ageInDays = (Date.now() - draftData.timestamp) / (1000 * 60 * 60 * 24);

          if (ageInDays > DRAFT_EXPIRY_DAYS) {
            localStorage.removeItem(key);
            console.log(`Cleaned up old draft: ${key}`);
          }
        } catch (error) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error cleaning up old drafts:', error);
    }
  },

  clearAllUserDrafts: (userId: string): void => {
    try {
      const keys = Object.keys(localStorage);
      const userDraftKeys = keys.filter(key =>
        key.startsWith(DRAFT_PREFIX) && key.includes(userId)
      );

      userDraftKeys.forEach(key => {
        localStorage.removeItem(key);
      });

      console.log(`Cleared ${userDraftKeys.length} drafts for user ${userId}`);
    } catch (error) {
      console.error('Error clearing user drafts:', error);
    }
  }
};

export const formatDraftAge = (milliseconds: number): string => {
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  return 'just now';
};
